<template>
	<div
		class="quick-actions-container"
		:class="{ 'add-contacts-container': addContacts }"
	>
		<button
			v-if="lightButton"
			class="btn-quick-actions"
			:class="{ active: showQuickActions, disabled: disabled }"
			:disabled="disabled"
			@click="toggleQuickActions"
			aria-label="Opens the Quick Actions Menu"
		>
			<font-awesome-icon
				:icon="lightButtonIcon"
				:class="{ disabled: disabled }"
			/>
			<span
				class="quick-actions-counter"
				v-if="showCounter && selectedContacts > 0"
				>{{ count }}</span
			>
		</button>
		<button
			v-if="darkButton"
			class="add-contacts__button b360-btn--primary"
			:class="{ disabled: disabled }"
			:disabled="disabled"
			@click="toggleQuickActions"
		>
			{{ buttonText }}
		</button>
		<transition name="fade">
			<div
				class="menu__container"
				v-if="showQuickActions"
				:class="{
					'menu__container--transformed': displayOnTop,
					'dark-button': darkButton,
					'align-left': alignLeft,
				}"
			>
				<p
					class="menu__container--empty"
					v-if="isMultiple && selectedContacts === 0"
				>
					Select items to see more actions
				</p>
				<div class="menu__container--menu-list" v-else>
					<ul
						class="menu__list"
						v-for="(menu, key) in menus"
						:key="key"
					>
						<template
							v-for="(option, index) in showMenuItems(menu)"
						>
							<li
								v-if="!option.hide"
								:key="index"
								role="button"
								@click="
									$emit(option.action);
									showQuickActions = false;
								"
								class="menu__list-item"
								:class="{
									'menu__list-item--disabled no-styles':
										option.disabled,
								}"
							>
								<span class="menu__list-icon">
									<font-awesome-icon
										class="list-icon"
										:class="
											option.iconColor
												? option.iconColor
												: ''
										"
										:icon="option.icon"
									/>
								</span>
								{{ option.text }}
							</li>
						</template>
					</ul>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
export default {
	name: 'QuickActionsMenu',
	props: {
		addContacts: Boolean,
		menus: Array,
		lightButton: Boolean,
		lightButtonIcon: Array,
		darkButton: Boolean,
		disabled: Boolean,
		selectedContacts: Number,
		showCounter: Boolean,
		buttonText: String,
		status: String,
		isMultiple: Boolean, // indicates if the quick actions are for multiple items
		alignLeft: {
			default: false,
			type: Boolean,
		},
	},
	data() {
		return {
			showQuickActions: false,
			displayOnTop: false,
		};
	},
	computed: {
		count() {
			return this.selectedContacts < 100 ? this.selectedContacts : '99+';
		},
	},
	methods: {
		toggleQuickActions() {
			this.showQuickActions = !this.showQuickActions;
		},
		clickHandler(e) {
			this.displayOnTop = e.y + 300 >= window.innerHeight;

			/* closes the quick action menu if you click anywhere off of it*/
			if (!this.$el.contains(e.target) && this.showQuickActions) {
				this.showQuickActions = false;
			}
		},
		showMenuItems(menu) {
			/* Filter out any options with the same status as this status */
			return this.status
				? menu.filter((option) => !this.status.includes(option.status))
				: menu;
		},
	},
	mounted() {
		window.addEventListener('click', this.clickHandler);
	},
	beforeDestroy() {
		window.removeEventListener('click', this.clickHandler);
	},
};
</script>

<style scoped lang="scss">
.quick-actions-container {
	position: relative;
}

.add-contacts__button {
	width: 100%;
	&.disabled {
		background-color: $b360-color--gray__medium-plus;
		cursor: default;
	}
}

.red {
	color: $error;
}

.green {
	color: $b360-color--green__success;
}

.btn-quick-actions {
	border: 1px solid $border-color-light;
	border-radius: 6px;
	color: $brand-blue;
	background-color: $white;
	transition: all 0.3s ease;
	position: relative;
	@include rem('font-size', 15px);
	@include rem('padding', 8px);
	@include rem('height', 34px);
	@include rem('width', 34px);

	&:hover,
	&:focus,
	&:active,
	&.active {
		background-color: $highlight-color;
		border: 1px solid $brand-blue;
		cursor: pointer;
	}
	&.disabled {
		color: $b360-color--gray__medium-plus;
		cursor: default;

		&:hover {
			background-color: $white;
			border: 1px solid $border-color-light;
		}
	}
}

.quick-actions-counter {
	background: $brand-blue;
	color: $white;
	font-weight: bold;
	line-height: 1;
	position: absolute;
	top: -13px;
	right: -13px;
	border-radius: 6px;
	display: flex;
	justify-content: center;
	align-items: center;
	@include rem('font-size', 14px);
	@include rem('width', 26px);
	@include rem('height', 26px);
}

.menu__container {
	box-sizing: border-box;
	background-color: $white;
	border-radius: $b360-border-radius-large;
	border: 1px solid $border-color-light;
	box-shadow: 0 3px 10px $shadow-color;
	z-index: 13;
	position: absolute;
	top: calc(100% + 10px);
	right: 0;
	opacity: 1;
	@include rem('min-width', 260px);

	p.menu__container--empty {
		padding-bottom: 0;
		line-height: 1.5;
		@include rem('padding', 0 15px);
	}

	@media only screen and (max-width: 600px) {
		.add-contacts-container & {
			width: 100%;
		}
	}
}
.menu__container--transformed {
	transform: translateY(-125%);
}

.menu__list {
	box-sizing: border-box;
	list-style: none;
	margin-top: 0;
	margin-bottom: 0;
	padding-left: 0;
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	@include rem('padding', 8px);

	& + & {
		border-top: 1px solid $border-color-light;
	}

	&-item {
		display: flex;
		width: 100%;
		background-color: $white;
		border-radius: $b360-border-radius-medium;
		line-height: 1 !important;
		transition: all 0.3s ease;
		@include rem('padding', 8px);

		&:hover {
			background-color: $header-color-light;
			color: $black;
			cursor: pointer;
		}

		&--disabled {
			color: $b360-color--gray__medium-plus;
			pointer-events: none;

			.menu__list-icon {
				color: $b360-color--gray__medium-plus;
			}
		}
	}

	.menu__list-item--disabled {
		.menu__list-icon {
			.list-icon {
				color: $b360-color--gray__medium-plus;
			}
		}
	}

	&-icon {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		flex: 0 0 18px;
		color: $black;
		@include rem('margin-right', 15px);
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}
.align-left {
	left: 0;
	right: unset;
}
@media only screen and (max-width: 600px) {
	.dark-button {
		left: 0;
		right: unset;
	}
}
</style>
